import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { useTranslation } from "react-i18next";

const SingleMutationChart = ({ item, formattedDate }) => {
  const { t } = useTranslation();

  const rawTypeName = item?.mutationType || item?.type || "";
  const typeName = rawTypeName.trim();

  // Extract counts dynamically from API item
  const approved = Number(item?.totalApproved ?? item?.approvedApplications ?? item?.approved ?? 0);
  const rejected = Number(item?.totalRejected ?? item?.rejectedApplications ?? item?.rejected ?? 0);
  const underProcess = Number(
    item?.totalInProgress ?? item?.inProgressApplications ?? item?.inProgress ?? item?.underProcess ?? 0
  );

  const total = approved + rejected + underProcess;

  const chartData = [
    {
      name: "Total Number of Application Approved",
      value: approved,
      color: "#3366cc",
    },
    {
      name: "Total Number of Application Rejected",
      value: rejected,
      color: "#dc3912",
    },
    {
      name: "Total Number of Application Under Process",
      value: underProcess,
      color: "#ff9900",
    },
  ];

  // Render percentage label inside slice for large slices (>= 7%)
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.07) return null; // Avoid crowding very small slices
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.58;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const pctVal = percent * 100;
    const formattedPct = pctVal % 1 === 0 ? `${pctVal.toFixed(0)}%` : `${pctVal.toFixed(1)}%`;

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="bold"
      >
        {formattedPct}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percent = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      return (
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #dcdcdc",
            borderRadius: "6px",
            padding: "8px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            fontSize: "13px",
          }}
        >
          <div style={{ fontWeight: "600", color: data.payload.color, marginBottom: "4px" }}>
            {data.name}
          </div>
          <div style={{ color: "#333" }}>
            Count: <strong>{data.value.toLocaleString()}</strong> ({percent}%)
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className="single-mutation-chart-card"
      style={{
        marginBottom: "36px",
        paddingBottom: "24px",
      }}
    >
      <h5
        style={{
          fontSize: "18px",
          fontWeight: "700",
          color: "#000000",
          marginBottom: "18px",
          letterSpacing: "-0.2px",
        }}
      >
        Application Status ({typeName.toUpperCase()}) as on {formattedDate}
      </h5>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          flexWrap: "wrap",
          gap: "36px",
        }}
      >
        {/* Pie Chart */}
        <div
          style={{
            width: "260px",
            height: "260px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {total > 0 ? (
            <PieChart width={260} height={260}>
              <Pie
                data={chartData}
                cx={130}
                cy={130}
                outerRadius={105}
                innerRadius={0}
                dataKey="value"
                stroke="#ffffff"
                strokeWidth={1.5}
                startAngle={90}
                endAngle={-270}
                labelLine={false}
                label={renderCustomizedLabel}
                isAnimationActive={false}
              >
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          ) : (
            <div
              style={{
                width: "210px",
                height: "210px",
                borderRadius: "50%",
                border: "2px dashed #d1d5db",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6b7280",
                fontSize: "13px",
              }}
            >
              {t("No applications")}
            </div>
          )}
        </div>

        {/* Custom Legend matching image */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: "16px",
            minWidth: "260px",
          }}
        >
          {chartData.map((legItem, legIdx) => (
            <div
              key={legIdx}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <span
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: legItem.color,
                  display: "inline-block",
                  marginTop: "4px",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  color: "#212529",
                  fontSize: "15px",
                  lineHeight: "1.4",
                  fontWeight: "500",
                }}
              >
                <div>{t(legItem.name)}</div>
                <div style={{ color: "#333333" }}>({legItem.value.toLocaleString()})</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MutationPieChart = ({ mutationData, loading }) => {
  const { t } = useTranslation();
  const [selectedFilter, setSelectedFilter] = useState("ALL");

  if (loading) {
    return (
      <div style={{ padding: "20px 0", color: "#6b7280" }}>
        <p className="mb-0">{t("Loading mutation application status charts...")}</p>
      </div>
    );
  }

  if (!mutationData || mutationData.length === 0) {
    return null;
  }

  // Dynamic date formatted as: "October 13, 2026"
  const formattedDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const displayedList =
    selectedFilter === "ALL"
      ? mutationData
      : mutationData.filter(
          (m) => (m?.mutationType || "").toLowerCase() === selectedFilter.toLowerCase()
        );

  return (
    <div
      className="mutation-pie-chart-wrapper"
      style={{
        backgroundColor: "#ffffff",
        padding: "16px 8px 16px 8px",
        marginBottom: "16px",
      }}
    >
      {/* Filter bar if multiple mutation types exist */}
      {mutationData.length > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingBottom: "12px",
            borderBottom: "1px solid #e5e7eb",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "14px", color: "#4b5563", fontWeight: "600" }}>
            {t("Mutation Types")}: {displayedList.length} of {mutationData.length}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <label style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#374151" }}>
              {t("Filter Type:")}
            </label>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="form-control form-control-sm"
              style={{
                width: "auto",
                display: "inline-block",
                fontSize: "13px",
                height: "32px",
                padding: "2px 8px",
                borderColor: "#d1d5db",
              }}
            >
              <option value="ALL">{t("All Types (Show All)")}</option>
              {mutationData.map((m, idx) => (
                <option key={idx} value={m.mutationType}>
                  {m.mutationType}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Render chart for each mutation type dynamically from API */}
      {displayedList.map((item, index) => (
        <SingleMutationChart
          key={item?.mutationType || index}
          item={item}
          formattedDate={formattedDate}
        />
      ))}
    </div>
  );
};

export default MutationPieChart;
